'use client'
import React from 'react'
import OffersCardClient from './OffersCardClient'
import { getLastUpdateDate, getProductDetailHref, splitHeading } from '@/constants/hooks'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'

interface Props {
    companyId: string
    mer_slug_type: string
    mer_slug: string
    bestOffers: any
    companyDomain: string
}

const BestOffersSSR = ({ companyId, mer_slug_type, mer_slug, bestOffers, companyDomain }: Props) => {
    const [firstHalf, secondHalf] = splitHeading(bestOffers?.popular_deals_widget?.widget_heading)
    const content = bestOffers?.popular_deals_widget?.widget_text

    const [sliderRef] = useKeenSlider<HTMLDivElement>({
        loop: true,
        mode: 'free-snap',
        slides: {
            perView: 4,
            spacing: 20,
        },
        breakpoints: {
            '(max-width: 1280px)': {
                slides: {
                    perView: 3,
                    spacing: 16,
                },
            },
            '(max-width: 1024px)': {
                slides: {
                    perView: 2,
                    spacing: 16,
                },
            },
            '(max-width: 640px)': {
                slides: {
                    perView: 1,
                    spacing: 12,
                },
            },
        },
        drag: true,
        rubberband: false,
        initial: 0,
    }, [
        (slider) => {
            let timeout: ReturnType<typeof setTimeout>
            let mouseOver = false

            function clearNextTimeout() {
                clearTimeout(timeout)
            }

            function nextTimeout() {
                clearTimeout(timeout)
                if (mouseOver) return
                timeout = setTimeout(() => {
                    slider.next()
                }, 5000)
            }

            slider.on('created', () => {
                slider.container.addEventListener('mouseover', () => {
                    mouseOver = true
                    clearNextTimeout()
                })
                slider.container.addEventListener('mouseout', () => {
                    mouseOver = false
                    nextTimeout()
                })
                nextTimeout()
            })
            slider.on('dragStarted', clearNextTimeout)
            slider.on('animationEnded', nextTimeout)
            slider.on('updated', nextTimeout)
        },
    ])

    if (bestOffers?.offers?.length > 0) {
        return (
            <section
              className="py-20 bg-gradient-to-br from-slate-50 to-indigo-50 overflow-hidden"
              aria-label="Popular deals and best offers"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 mb-10">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 mb-3"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" /><span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em]">Best Offers</span></span>
                    <div className="flex flex-col items-center mb-12 border-l-4 border-indigo-600 pl-6">
                        <div className="w-full">
                            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">
                                <span className="text-slate-900">{firstHalf ? firstHalf : `Popular`} </span>
                                <span className="text-indigo-600">{secondHalf ? secondHalf : `Deals`}</span>
                            </h2>
                            <div>
                                <p className="text-md text-slate-500 font-medium mb-0 max-w-sm sm:max-w-none text-balance">
                                    {content}
                                </p>
                                <p className="text-sm text-slate-900 font-bold mt-2 max-w-sm sm:max-w-none text-balance">
                                    This Week&#39;s Hottest Deals — Verified on {getLastUpdateDate(1)} by Our Team.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 -my-10">
                    <div className="relative group py-10">
                        <div ref={sliderRef} className="keen-slider py-10">
                            {bestOffers?.offers?.map((item: any, i: number) => (
                                <div key={item?.offer?.unique_id || item?.offer?.id || i} className="keen-slider__slide hover:z-20 relative">
                                    <OffersCardClient
                                        offer={item}
                                        mer_slug_type={mer_slug_type}
                                        mer_slug={mer_slug}
                                        productDetailUrl={item?.offer?.slug ? getProductDetailHref(item?.merchant, mer_slug_type, item?.offer?.slug, item?.offer?.category?.slug) : null}
                                        domain={companyDomain}
                                        companyId={companyId}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    return null
}

export default BestOffersSSR